import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useFavorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Fetch all favorites
  const fetchFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavorites([]);
      setFavoriteIds(new Set());
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/client/favorites`);
      const favoritesList = response.data || [];
      setFavorites(favoritesList);
      setFavoriteIds(new Set(favoritesList.map((fav) => fav.propertyId)));
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setFavorites([]);
      setFavoriteIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Check if a property is favorite
  const isFavorite = useCallback(
    (propertyId) => {
      return favoriteIds.has(propertyId);
    },
    [favoriteIds],
  );

  // Add favorite with optimistic update
  const addFavorite = useCallback(
    async (propertyId) => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      // Optimistic update
      const previousFavorites = [...favorites];
      const previousIds = new Set(favoriteIds);
      
      setFavoriteIds((prev) => new Set([...prev, propertyId]));

      try {
        const response = await axios.post(`${API}/client/favorites/${propertyId}`);
        // Refresh favorites to get the full data
        await fetchFavorites();
        return { success: true, data: response.data };
      } catch (error) {
        // Rollback on error
        setFavoriteIds(previousIds);
        setFavorites(previousFavorites);
        console.error('Error adding favorite:', error);
        throw error;
      }
    },
    [isAuthenticated, navigate, favorites, favoriteIds, fetchFavorites],
  );

  // Remove favorite with optimistic update
  const removeFavorite = useCallback(
    async (propertyId) => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      // Optimistic update
      const previousFavorites = [...favorites];
      const previousIds = new Set(favoriteIds);
      
      setFavoriteIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(propertyId);
        return newSet;
      });
      setFavorites((prev) => prev.filter((fav) => fav.propertyId !== propertyId));

      try {
        await axios.delete(`${API}/client/favorites/${propertyId}`);
        return { success: true };
      } catch (error) {
        // Rollback on error
        setFavoriteIds(previousIds);
        setFavorites(previousFavorites);
        console.error('Error removing favorite:', error);
        throw error;
      }
    },
    [isAuthenticated, navigate, favorites, favoriteIds],
  );

  // Toggle favorite
  const toggleFavorite = useCallback(
    async (propertyId) => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      if (isFavorite(propertyId)) {
        return await removeFavorite(propertyId);
      } else {
        return await addFavorite(propertyId);
      }
    },
    [isAuthenticated, navigate, isFavorite, addFavorite, removeFavorite],
  );

  // Check favorite status (optional, for initial load)
  const checkFavorite = useCallback(
    async (propertyId) => {
      if (!isAuthenticated) {
        return { isFavorite: false };
      }

      try {
        const response = await axios.get(`${API}/client/favorites/check/${propertyId}`);
        return response.data;
      } catch (error) {
        console.error('Error checking favorite:', error);
        return { isFavorite: false };
      }
    },
    [isAuthenticated],
  );

  // Load favorites on mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    } else {
      setFavorites([]);
      setFavoriteIds(new Set());
    }
  }, [isAuthenticated, fetchFavorites]);

  return {
    favorites,
    favoriteIds: Array.from(favoriteIds),
    loading,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    checkFavorite,
    fetchFavorites,
  };
};

